// web/src/components/loader/LoaderScene.tsx
// v2 processing visual. Composes the full-viewport Unicorn WebGL background (star + beam +
// swirl, driven by charge) with the DOM cover (stack + shake + glow), the quip write-on
// stream, and the ProgMeter Rive progress meter anchored to the bottom. Fully controlled:
// the parent owns charge/count/phase/quip. Fires onIgnited once on phase="ignite" with a
// failsafe (the ignition explosion + handoff live in Processing / IgnitionOverlay).
//
// Entrance sequencing: the COVER is the view-transition shared element (must exist
// synchronously so the source-thumb morph has its target) — everything that arrives async
// (the WebGL scene, the Rive meter, the quips) used to pop in mid-morph and jank the
// transition. Those layers now hold at opacity-0 until the VT window has passed (and, for
// the WebGL, until the scene has actually loaded), then fade in on a short stagger.
import { useEffect, useState } from "react";
import type { JSX } from "react";
import type { Mode } from "@/types/roast";
import type { DetectedSourceType } from "@/lib/createFlow";
import { UnicornBeam } from "./UnicornBeam";
import { CoverStack } from "./CoverStack";
import { QuipBubbles } from "./QuipBubbles";
import { RiveMeter } from "./RiveMeter";

// Covers the router view-transition morph (~250ms default cross-fade/morph) plus slack.
// Heavy layers are not just hidden but NOT MOUNTED until this passes: creating the WebGL
// context / compiling Unicorn shaders / initializing the Rive WASM runtime are main-thread
// stalls that jank the morph even at opacity-0 — the first paint of this screen must be
// nothing but black + the cover (the morph target) + the cancel button.
// Exported: Processing defers its Tone.js synth/music start by the same window.
export const VT_SETTLE_MS = 350;

export function LoaderScene({
  coverUrl, mode, charge, phase, quip, onIgnited,
}: {
  coverUrl: string | null;
  detectedType: DetectedSourceType; // carried by callers; the meter maps charge itself
  mode: Mode;
  charge: number;
  count?: { done: number; total: number }; // accepted from callers; not surfaced (the meter is the progress)
  phase: "charging" | "ignite";
  quip?: string;
  onIgnited?: () => void;
}): JSX.Element {
  // Entrance gates: `settled` = the view-transition window has passed; `bgLoaded` = the
  // Unicorn scene has rendered (its SDK + JSON load async and would otherwise pop in).
  const [settled, setSettled] = useState(false);
  const [bgLoaded, setBgLoaded] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setSettled(true), VT_SETTLE_MS);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (phase !== "ignite") return;
    let fired = false;
    const fire = (): void => { if (!fired) { fired = true; onIgnited?.(); } };
    const id = setTimeout(fire, 800); // failsafe (the ignition explosion lives in IgnitionOverlay)
    return () => clearTimeout(id);
  }, [phase, onIgnited]);

  return (
    <div className="relative h-[100dvh] w-full overflow-hidden bg-black text-white" data-testid="processing-scene">
      {/* Unicorn WebGL fills the whole viewport. MOUNTED only after the VT window (context
          creation + shader compile would jank the cover morph), then revealed only once the
          scene has actually rendered — never a black-canvas fade-in. */}
      <div
        data-testid="webgl-fade"
        className={`absolute inset-0 transition-opacity duration-300 ${settled && bgLoaded ? "opacity-100" : "opacity-0"}`}
      >
        {settled && <UnicornBeam charge={charge} onReady={() => setBgLoaded(true)} />}
      </div>
      {/* Cover = the view-transition shared element — never gated/delayed. */}
      <CoverStack coverUrl={coverUrl} mode={mode} charge={charge} phase={phase} />
      <div
        data-testid="quips-fade"
        className={`transition-opacity duration-300 delay-200 ${settled ? "opacity-100" : "opacity-0"}`}
      >
        <QuipBubbles quip={quip} />
      </div>

      {/* ProgMeter Rive meter. Desktop lifts it off the bottom; the mobile artboard
          (RiveMeter swaps it at ≤768px) is anchored flush to the bottom of the viewport —
          the media variant matches RiveMeter's own (max-width: 768px) switch. */}
      <div
        data-testid="meter-fade"
        className={`pointer-events-none absolute inset-x-0 bottom-[9vh] [@media(max-width:768px)]:bottom-0 z-10 h-[24vh] max-h-60 transition-opacity duration-300 delay-150 ${settled ? "opacity-100" : "opacity-0"}`}
      >
        {settled && <RiveMeter charge={charge} phase={phase} />}
      </div>
    </div>
  );
}
